// eslint-disable-next-line
import * as Sentry from '@sentry/browser';
// eslint-disable-next-line
import * as Integrations from '@sentry/integrations';
import Vue from 'vue';
import { cloneDeep } from 'lodash';

import config from '../config';

export default class Logger {
  constructor(env, integrateSentryWithVue = true) {
    this.LOCAL = 'local';
    this.DEV = 'development';
    this.STAGING = 'staging';
    this.DEMO = 'demo';
    this.PROD = 'production';
    this.env = env;
    if (integrateSentryWithVue) {
      Logger.initSentry(integrateSentryWithVue);
    }
  }

  static initSentry(integrateSentryWithVue = true) {
    let integrations = [];
    // If Sentry is integrated with Vue then Sentry will set the Vue global error handler. This
    // causes Vue-test-utils to not set it's global error handler. That causes two issues. First,
    // it will print out an error message on the console when running the cypress even though the
    // cypress will pass. Second, the cypress can actually be failing but jest doesn't seem to
    // recognize that there was an error thrown so it will treat it as passed.
    //
    // So, if we are running unit cypress do not integrate with Vue. This is the only scenario
    // where integrateSentryWithVue should be false.
    if (integrateSentryWithVue) {
      integrations = [
        new Integrations.Vue({ Vue, attachProps: true, logErrors: true }),
        new Integrations.CaptureConsole({
          levels: ['error'],
        }),
      ];
    }

    Sentry.init({
      dsn: 'https://391979db4c3c4c3fb879b835ffec49d1@sentry.io/299431',
      integrations,
      environment: config.Node,
      ignoreErrors: [
        'CodeMismatchException',
        'UserNotFoundException',
        'NotAuthorizedException',
        'InvalidParameterException',
        'UserNotConfirmedException',
        'ExpiredCodeException',
        'LimitExceededException',
        'User does not exist',
        'Incorrect username or password',
        'Username/client id combination not found',
        'An account with the given email already exists'
      ],
      beforeSend(event) {
        if (config.Node === 'development') {
          // TODO: Do we still want to print this error here now
          // that we're use logErrors=true in integrations?
          console.error(event);
          return null;
        }

        return event;
      },
    });
  }

  log(...params) {
    if (this.env === this.DEV || this.env === this.LOCAL) {
      console.log(...params);
    }
  }

  // eslint-disable-next-line
  capture(e) {
    const error = Logger.getDetailedError(e);
    try {
      Sentry.captureException(error);
    } catch (er) {
      console.log(er);
    }
  }

  static getDetailedError(e) {
    const error = cloneDeep(e);
    if (error instanceof Error) {
      if (error.response && !error.response.data.startsWith('<html>')) {
        error.message = `${error.message} (${error.response.data})`;
      }
    }
    return error;
  }

  processError(e) {
    if (e === null) {
      return null;
    }

    if (e instanceof Error) {
      /* It's an error from the API, so show the response message */
      if (e.response && !e.response.data.startsWith('<html>')) {
        return e.response.data;
      }
      /* It's a runtime error, so send it to Sentry and apologize to the user */

      this.capture(e);
      return 'There was an unknown error. Our team has been alerted and are looking into it.';
    }

    /* Programmer supplied his own error message, so just show it */
    if (typeof e === 'string') {
      return e;
    }

    return null;
  }

  convertNonErrorExceptionToError(exception) {
    let error = exception;
    if (error.code) {
      const message = `${this.env} - ${error.name}: ${error.message}`;
      error = new Error(message);
    }
    return error;
  }

  // eslint-disable-next-line
  setUserContext(context) {
    // this.raven.setUserContext(context);
    if (context) {
      Sentry.setUser({
        email: context.email,
        id: context.memberId,
        username: context.email,
      });
      Sentry.setExtra('name', context.name);
      Sentry.setExtra('member_type', context.member_type);
      Sentry.setExtra('cognito_user_id', context.cognito_user_id);
    } else {
      Sentry.configureScope(scope => scope.clear());
    }
  }
}
